import { AJUDA_ARQUIVOS } from "@/lib/ajuda/dialogos/arquivos";
import { lazy, Suspense, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { formatFileSize } from "@/lib/files-format";
import { fileIconFor, type FileRow } from "./files-types";
import { ImagemViewer } from "./ImagemViewer";

// `three` é pesado (~600KB) — só entra no bundle de quem realmente abre um arquivo 3D, não no
// carregamento inicial de todo mundo que nunca anexa STL/OBJ/PLY.
const Viewer3D = lazy(() => import("./Viewer3D").then((m) => ({ default: m.Viewer3D })));

const EXTENSOES_3D = ["stl", "obj", "ply"];

function extensaoDoArquivo(name: string): string {
  const partes = name.split(".");
  return partes.length > 1 ? (partes.pop() ?? "").toLowerCase() : "";
}

/** Visualização inline pra imagem/PDF — o resto cai no fallback de baixar direto.
 *
 *  Renderiza com base no `contentType` salvo, e isso só é seguro porque o backend valida o CONTEÚDO
 *  real contra o tipo declarado no `file.complete` (`matchesDeclaredType`, por assinatura de bytes)
 *  para anexos. Atenção: essa validação cobre `PROSTHESIS_ATTACHMENT`, não `GENERIC` — arquivo do
 *  Drive pode ter qualquer tipo declarado, e o que impede execução ali é o download vir com
 *  `Content-Disposition: attachment` e de outro origin, não este componente. */
export function FilePreviewDialog({ file, onClose }: { file: FileRow | null; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const utils = trpc.useUtils();

  const isImage = file?.contentType.startsWith("image/") ?? false;
  const isPdf = file?.contentType === "application/pdf";
  // STL/OBJ/PLY quase sempre chegam com contentType genérico (octet-stream) — a extensão do
  // nome é o único jeito confiável de saber que é um modelo 3D, igual fizemos na validação do upload.
  const extensao = file ? extensaoDoArquivo(file.name) : "";
  const is3D = EXTENSOES_3D.includes(extensao);
  const canPreview = isImage || isPdf || is3D;

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    if (!canPreview) return;

    let cancelled = false;
    setLoading(true);
    utils.client.file.getDownloadUrl
      .query({ fileId: file.id })
      .then(({ url: downloadUrl }) => {
        if (!cancelled) setUrl(downloadUrl);
      })
      .catch(() => {
        if (!cancelled) toast.error("Não foi possível carregar a prévia.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  async function handleDownload() {
    if (!file) return;
    try {
      const { url: downloadUrl } = await utils.client.file.getDownloadUrl.query({ fileId: file.id });
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Não foi possível baixar o arquivo.");
    }
  }

  const Icon = file ? fileIconFor(file.contentType) : null;
  // Imagem e 3D usam viewer próprio (zoom/pan/contraste, ou câmera orbital) — precisam de mais
  // espaço que a prévia simples de PDF.
  const dialogWide = isImage || is3D;

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={dialogWide ? "max-w-5xl bg-white dark:bg-card" : "max-w-3xl bg-white dark:bg-card"}>
        <DialogHeader>
          <DialogTitle className="truncate" ajuda={AJUDA_ARQUIVOS.previa}>{file?.name}</DialogTitle>
        </DialogHeader>

        {!canPreview ? (
          <div className="flex flex-col items-center gap-3 py-12">
            {Icon && <Icon className="h-10 w-10 text-zinc-300 dark:text-muted-foreground" />}
            <p className="text-sm text-zinc-500 dark:text-muted-foreground">
              Sem prévia disponível pra esse tipo de arquivo.
            </p>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Download className="h-4 w-4" />
              Baixar {file && formatFileSize(file.size)}
            </button>
          </div>
        ) : loading || !url ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : isImage ? (
          <div className="h-[75vh]">
            <ImagemViewer src={url} alt={file?.name} fileName={file?.name} onDownload={handleDownload} embedded />
          </div>
        ) : is3D ? (
          <div className="h-[75vh]">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                </div>
              }
            >
              <Viewer3D fileUrl={url} fileName={file?.name ?? "modelo"} fileExtension={extensao} embedded />
            </Suspense>
          </div>
        ) : (
          <embed src={url} type="application/pdf" className="h-[70vh] w-full rounded-md" />
        )}
      </DialogContent>
    </Dialog>
  );
}
